import { styled } from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import {
  GRAYPALEBORDER,
  GRAYPALECONTENTS,
  GREENBOX,
  GREENCONTENTS,
  GREENTITLE,
} from "../../../styles/color";
import { useSasToken } from "../../../hooks/useSasToken";
import { VehiclePhotoInfo } from "../../../system/types/Vehicle";
import PhotoLightbox, { ThumbStrip } from "../../Common/Photo/PhotoLightbox";
import {
  ANGLE_LABEL,
  PHOTO_PHASE_LABEL,
  PHOTO_PHASE_SHORT,
  REQUIRED_ANGLES,
} from "./vehiclePhoto";
import {
  PhotoSets,
  ViewerTarget,
  etcItems,
  stepTarget,
  targetPhoto,
  togglePhase,
} from "./photoViewerModel";
import { photoTimeLabel } from "../../Common/Photo/photoTime";

interface VehiclePhotoZoomProps {
  target: ViewerTarget;
  sets: PhotoSets;
  isMobile: boolean;
  onChange: (target: ViewerTarget) => void;
  onClose: () => void;
}

/**
 * 차량 사진 크게 보기. 같은 방향의 출발/도착 사진을 한 자리에서 번갈아 보며 흠집을 찾는 화면.
 * 확대·이동·넘기기·닫기는 공통 PhotoLightbox 가 처리하고, 여기서는 무엇을 보여줄지만 정한다.
 * - 방향 사진: 좌우로 앞/뒤/좌/우를 넘기고, 출발/도착 사진 전환 버튼(스페이스)으로 같은 방향을 비교
 * - 추가 사진: 좌우와 아래 썸네일 줄로 추가 사진끼리 넘긴다
 */
function VehiclePhotoZoom({ target, sets, isMobile, onChange, onClose }: VehiclePhotoZoomProps) {
  const { appendSas } = useSasToken();
  const { phase, photo } = targetPhoto(target, sets);
  const etc = etcItems(sets);

  const title =
    target.kind === "angle"
      ? `${ANGLE_LABEL[target.angle]} · ${PHOTO_PHASE_LABEL[phase]}`
      : `추가 · ${PHOTO_PHASE_LABEL[phase]}`;
  const count =
    target.kind === "angle"
      ? `${REQUIRED_ANGLES.indexOf(target.angle) + 1} / ${REQUIRED_ANGLES.length}`
      : `${target.index + 1} / ${etc.length}`;

  return (
    <PhotoLightbox
      title={title}
      subtitle={photo ? photoTimeLabel(photo, true) : "사진 없음"}
      imageUrl={photo ? appendSas(photo.url) : undefined}
      downloadName={photo?.name}
      emptyText={`${PHOTO_PHASE_LABEL[phase]}이 없습니다`}
      isMobile={isMobile}
      onPrev={() => onChange(stepTarget(target, -1, sets))}
      onNext={() => onChange(stepTarget(target, 1, sets))}
      onClose={onClose}
      onExtraKey={(event) => {
        if (event.key !== " " || target.kind !== "angle") return false;
        onChange(togglePhase(target));
        return true;
      }}
      navLabel={target.kind === "angle" ? "방향 이동" : "사진 넘기기"}
      extraHints={
        target.kind === "angle" ? [{ keys: ["Space"], label: "출발/도착 사진 전환" }] : []
      }
      count={count}
      toolbar={
        target.kind === "angle" && (
          <PhaseToggle role="group" aria-label="출발/도착 사진 전환">
            {(["START", "END"] as const).map((value) => (
              <PhaseButton
                key={value}
                type="button"
                selected={target.phase === value}
                onClick={() => onChange({ ...target, phase: value })}
              >
                <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                {PHOTO_PHASE_LABEL[value]}
              </PhaseButton>
            ))}
          </PhaseToggle>
        )
      }
      strip={
        target.kind === "etc" && (
          <ThumbStrip
            items={etc.map((item, index) => ({
              key: item.photo.id,
              url: appendSas(item.photo.url),
              badge: PHOTO_PHASE_SHORT[item.phase],
              label: `${PHOTO_PHASE_SHORT[item.phase]} 추가 사진 ${index + 1}`,
            }))}
            selectedIndex={target.index}
            onSelect={(index) => onChange({ kind: "etc", index })}
          />
        )
      }
    />
  );
}

export default VehiclePhotoZoom;

export function photoSetsOf(start?: VehiclePhotoInfo[], end?: VehiclePhotoInfo[]): PhotoSets {
  return { START: start ?? [], END: end ?? [] };
}

const PhaseToggle = styled("div")({
  display: "inline-flex",
  border: `1px solid ${GRAYPALEBORDER}`,
  borderRadius: "8px",
  overflow: "hidden",
});

const PhaseButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ selected }) => ({
  // 실제 동작 버튼(꽉 찬 초록)과 구별되게, 비교 화면의 방향 탭과 같은 '보기 전환' 모양으로 그린다
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  border: 0,
  padding: "8px 14px",
  fontSize: "0.9rem",
  cursor: "pointer",
  backgroundColor: selected ? GREENBOX : "#fff",
  color: selected ? GREENTITLE : GRAYPALECONTENTS,
  boxShadow: selected ? `inset 0 0 0 1px ${GREENCONTENTS}` : "none",
  fontWeight: selected ? 600 : 400,
}));
